import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';
import { PlusIcon, XIcon } from 'lucide-react';
import { createContext, useContext, type ComponentPropsWithRef, type ReactElement } from 'react';
import { cn } from '../util/classes.ts';

/** Shared silhouette of both controls. */
const pillBase = [
    'select-none inline-flex shrink-0 items-center whitespace-nowrap rounded-full',
    'h-interactive border border-line text-ui-base',
    'transition duration-state ease-state',
    'state-disabled:pointer-events-none state-disabled:opacity-disabled',
];

export type AddFilterButtonProps = useRender.ComponentProps<'button'>;

/**
 * The "add a filter" trigger; the localized label goes in children. Dashed means
 * "something can be added" — keep it solid on a trigger that only re-opens a set.
 */
export function AddFilterButton({
    className,
    children,
    render,
    ...props
}: AddFilterButtonProps): ReactElement {
    return useRender({
        defaultTagName: 'button',
        render,
        props: {
            // Explicit — a bare button defaults to type="submit".
            type: 'button',
            'data-slot': 'add-filter-button',
            ...props,
            className: cn(
                pillBase,
                'cursor-pointer gap-cozy border-dashed text-content',
                'pl-cozy pr-default',
                'hover:bg-content/(--opacity-wash) active:translate-px',
                "*:[svg:not([class*='size-'])]:size-4 *:[svg]:shrink-0 *:[svg]:text-content-weaker",
                className,
            ),
            children: (
                <>
                    <PlusIcon />
                    {children}
                </>
            ),
        },
    });
}

const FilterPillLabelContext = createContext('');

/** `muted` de-emphasizes a pill among other applied pills — it does NOT mean "not applied". */
const filterPillVariants = cva(
    [
        ...pillBase,
        'min-w-0 gap-tight',
        // Trailing padding only reserves room for the ×.
        'pl-default pr-tight not-has-data-[slot=filter-pill-remove]:pr-default',
        // Hover/press only on interactive parts: a button body, or the pill
        // itself rendered as a button. The ×'s chrome is per variant below.
        'has-[button[data-slot=filter-pill-body]:active]:translate-px',
        '[&:where(button)]:cursor-pointer [&:where(button)]:active:translate-px',
    ],
    {
        variants: {
            variant: {
                // Raw blue-* per the mock — no brand-chip token yet.
                brand: [
                    'border-blue-300 bg-blue-50 text-blue-700',
                    'has-[button[data-slot=filter-pill-body]:hover]:bg-blue-100',
                    '[&:where(button)]:hover:bg-blue-100',
                    '**:data-[slot=filter-pill-remove]:text-blue-400',
                    '**:data-[slot=filter-pill-remove]:hover:bg-blue-100 **:data-[slot=filter-pill-remove]:hover:text-blue-700',
                ],
                muted: [
                    'text-content-weak',
                    'has-[button[data-slot=filter-pill-body]:hover]:bg-content/(--opacity-wash)',
                    '[&:where(button)]:hover:bg-content/(--opacity-wash)',
                    '**:data-[slot=filter-pill-remove]:text-content-weaker',
                    '**:data-[slot=filter-pill-remove]:hover:bg-content/(--opacity-wash) **:data-[slot=filter-pill-remove]:hover:text-content',
                ],
            },
        },
        defaultVariants: {
            variant: 'brand',
        },
    },
);

export type FilterPillVariantProps = VariantProps<typeof filterPillVariants>;

export type FilterPillProps = useRender.ComponentProps<'span'> &
    FilterPillVariantProps & {
        /** Facet name, e.g. "Kunden" — `FilterPillBody` renders it as "label: value". Empty ⇒ children only. */
        label: string;
    };

/** One applied filter: `FilterPillBody` + optional `FilterPillRemove` as children; `render={<button/>}` makes the whole pill interactive. */
export function FilterPill({ label, variant, className, render, ...props }: FilterPillProps): ReactElement {
    const element = useRender({
        defaultTagName: 'span',
        render,
        props: {
            'data-slot': 'filter-pill',
            ...props,
            className: cn(filterPillVariants({ variant }), className),
        },
    });
    return <FilterPillLabelContext.Provider value={label}>{element}</FilterPillLabelContext.Provider>;
}

export type FilterPillBodyProps = useRender.ComponentProps<'span'>;

/**
 * The pill's text region, rendered as "label: children". Inert by default — pass
 * `render={<button type="button" />}` with an `onClick` to re-open the facet editor.
 */
export function FilterPillBody({ className, children, render, ...props }: FilterPillBodyProps): ReactElement {
    const label = useContext(FilterPillLabelContext);

    return useRender({
        defaultTagName: 'span',
        render,
        props: {
            'data-slot': 'filter-pill-body',
            ...props,
            className: cn(
                'flex h-full min-w-0 items-center rounded-full',
                '[&:where(button)]:cursor-pointer',
                className,
            ),
            // The text needs its own block to ellipsize; no label means no stray colon.
            children: (
                <span className="min-w-0 truncate">
                    {label ? (
                        <>
                            {label}: {children}
                        </>
                    ) : (
                        children
                    )}
                </span>
            ),
        },
    });
}

export type FilterPillRemoveProps = ComponentPropsWithRef<'button'> & {
    /** Required — the accessible name has to be localized by the caller. */
    'aria-label': string;
};

/** The trailing × that removes this filter. */
export function FilterPillRemove({ className, ...props }: FilterPillRemoveProps): ReactElement {
    return (
        <button
            type="button"
            data-slot="filter-pill-remove"
            className={cn(
                'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full p-0.5',
                'transition duration-state ease-state active:translate-px',
                className,
            )}
            {...props}
        >
            <XIcon className="size-3.5" />
        </button>
    );
}
